import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/layout/AdminLayout';
import { companyAPI, settlementAPI } from '../../services/api';
import getImageUrl from '../../utils/imageUrl';

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [users, setUsers] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [shops, setShops] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  
  // Хугацааны filter
  const [dateRange, setDateRange] = useState('today');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

   useEffect(() => {
    fetchCompanyDetails();
  }, [id, dateRange, customStartDate, customEndDate]);

  const fetchCompanyDetails = async () => {
    try {
      setLoading(true);
      
      // Query params бэлдэх
      let params = {};
      if (dateRange === 'custom' && customStartDate && customEndDate) {
        params = { start_date: customStartDate, end_date: customEndDate };
      } else {
        params = { date_range: dateRange };
      }
      
      const response = await companyAPI.getById(id, params);
      const data = response.data.data;
      setCompany(data.company);
      setUsers(data.users || []);
      setWarehouses(data.warehouses || []);
      setShops(data.shops || []);
      setStats(data.stats || {});
    } catch (error) {
      console.error('Компанийн дэлгэрэнгүй татахад алдаа:', error);
      alert('Мэдээлэл татахад алдаа гарлаа');
      navigate('/admin/companies');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/admin/companies?edit=${id}`);
  };

  const handleResetPassword = async () => {
    if (!confirm('Компанийн админы нууц үгийг шинэчлэх үү?')) return;
    try {
      const response = await companyAPI.resetPassword(id);
      const credentials = response.data.data.credentials;
      alert(`✅ Нууц үг шинэчлэгдлээ!\n\nНэвтрэх нэр: ${credentials.username}\nНууц үг: ${credentials.password}\n\n${credentials.message}`);
    } catch (error) {
      console.error('Нууц үг шинэчлэхэд алдаа:', error);
      alert('❌ Нууц үг шинэчлэхэд алдаа гарлаа');
    }
  };

  const handleDelete = async () => {
    if (!confirm('Энэ компанийг устгахдаа итгэлтэй байна уу?')) return;
    try {
      await companyAPI.delete(id);
      alert('✅ Компани амжилттай устлаа!');
      navigate('/admin/companies');
    } catch (error) {
      console.error('Устгахад алдаа:', error);
      const errorMessage = error.response?.data?.message || 'Устгахад алдаа гарлаа';
      alert('❌ ' + errorMessage);
    }
  };

   const handlePayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      alert('❌ Дүн буруу байна');
      return;
    }
    if (amount > (stats.unpaidCommission || 0)) {
      alert('❌ Төлөх дүн нь өглөгөөс их байна');
      return;
    }
    try {
      const response = await settlementAPI.recordCompanyPayment(id, { amount: amount });
      
      if (response.data.success) {
        alert(`✅ ${amount.toLocaleString()}₮ төлбөр бүртгэгдлээ!`);
        setShowPaymentModal(false);
        setPaymentAmount('');
        fetchCompanyDetails();
      } else {
        alert('❌ ' + (response.data.message || 'Төлбөр бүртгэхэд алдаа гарлаа'));
      }
    } catch (error) {
      console.error('Төлбөр бүртгэхэд алдаа:', error);
      alert('❌ ' + (error.response?.data?.message || 'Төлбөр бүртгэхэд алдаа гарлаа'));
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!company) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-xl text-gray-600">Компани олдсонгүй</p>
        </div>
      </AdminLayout>
    );
  }

  const todayDeliveries = stats.todayDeliveries || 0;
  const todayCompleted = stats.todayCompleted || 0;
  const todayPending = todayDeliveries - todayCompleted;
  const successRate = todayDeliveries > 0 ? Math.round((todayCompleted / todayDeliveries) * 100) : 0;
  const unpaidCommission = stats.unpaidCommission || 0;
  const remaining = unpaidCommission - (parseFloat(paymentAmount) || 0);

  return (
    <AdminLayout>
      <div className="mb-6">
        <button onClick={() => navigate('/admin/companies')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          <span>Буцах</span>
        </button>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-gray-50 rounded-xl flex items-center justify-center border border-gray-200">
              {company.logo_thumbnail || company.logo_url ? (
                <img src={getImageUrl(company.logo_thumbnail || company.logo_url)} alt={company.company_name} className="max-w-full max-h-full object-contain p-2" />
              ) : (
                <span className="text-3xl">🏢</span>
              )}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{company.company_name}</h1>
              <div className="flex items-center gap-3">
                <span className={`badge ${company.status === 'active' ? 'badge-success' : company.status === 'suspended' ? 'badge-warning' : 'badge-gray'}`}>
                  {company.status === 'active' ? 'Идэвхтэй' : company.status === 'suspended' ? 'Түр хаагдсан' : 'Идэвхгүй'}
                </span>
                <span className="text-sm text-gray-500">РД: {company.register_number}</span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={handleEdit} className="btn bg-blue-50 text-blue-600 hover:bg-blue-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
              <span>Засах</span>
            </button>
            <button onClick={handleResetPassword} className="btn bg-purple-50 text-purple-600 hover:bg-purple-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
              <span>Нууц үг</span>
            </button>
            <button onClick={handleDelete} className="btn bg-red-50 text-red-600 hover:bg-red-100">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              <span>Устгах</span>
            </button>
          </div>
        </div>
      </div>

       {/* ============================================ */}
      {/* Хугацааны Filter */}
      {/* ============================================ */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm font-medium text-gray-700">📅 Хугацаа:</span>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'today', label: 'Өнөөдөр' },
              { value: 'yesterday', label: 'Өчигдөр' },
              { value: 'week', label: '7 хоног' },
              { value: 'month', label: 'Энэ сар' },
              { value: 'custom', label: 'Сонгох' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => setDateRange(option.value)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  dateRange === option.value
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
          
          {dateRange === 'custom' && (
            <div className="flex items-center gap-2 ml-4">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-gray-500">-</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          )}
          
          {loading && (
            <div className="ml-auto flex items-center gap-2 text-blue-600">
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="text-sm">Уншиж байна...</span>
            </div>
          )}
        </div>
      </div>

      {/* ============================================ */}
      {/* Статистик картууд - Шинэ дизайн */}
      {/* ============================================ */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* Нийт хүргэлт */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl">📦</span>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{stats.totalDeliveries || stats.todayDeliveries || 0}</div>
              <div className="text-blue-100 text-sm">Нийт хүргэлт</div>
            </div>
          </div>
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div className="h-full bg-white/60 rounded-full" style={{ width: '100%' }}></div>
          </div>
        </div>

        {/* Амжилттай */}
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl">✅</span>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{stats.completedDeliveries || stats.todayCompleted || 0}</div>
              <div className="text-green-100 text-sm">Амжилттай</div>
            </div>
          </div>
          <div className="text-green-100 text-xs">
            {(stats.totalDeliveries || stats.todayDeliveries) > 0 
              ? Math.round(((stats.completedDeliveries || stats.todayCompleted || 0) / (stats.totalDeliveries || stats.todayDeliveries)) * 100)
              : 0}% амжилт
          </div>
        </div>

        {/* Хүлээгдэж буй */}
        <div className="bg-gradient-to-br from-yellow-500 to-orange-500 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl">⏳</span>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{stats.pendingDeliveries || ((stats.todayDeliveries || 0) - (stats.todayCompleted || 0))}</div>
              <div className="text-yellow-100 text-sm">Хүлээгдэж буй</div>
            </div>
          </div>
          <div className="text-yellow-100 text-xs">Хүргэлт хийгдээгүй</div>
        </div>

        {/* Цуцлагдсан */}
        <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-2xl p-5 text-white shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
              <span className="text-2xl">❌</span>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold">{stats.cancelledDeliveries || 0}</div>
              <div className="text-red-100 text-sm">Цуцлагдсан</div>
            </div>
          </div>
          <div className="text-red-100 text-xs">Буцаагдсан, амжилтгүй</div>
        </div>
      </div>

      {/* Шимтгэл & Төлбөрийн карт */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Шимтгэлийн мэдээлэл */}
        <div className={`rounded-2xl p-5 shadow-lg transition-all hover:-translate-y-1 ${
          (stats.unpaidCommission || 0) > 0 
            ? 'bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-200' 
            : 'bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-200'
        }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                (stats.unpaidCommission || 0) > 0 ? 'bg-red-200' : 'bg-green-200'
              }`}>
                <span className="text-2xl">💰</span>
              </div>
              <div>
                <div className="text-sm text-gray-600">
                  {stats.pricingModel === 'monthly_fixed' ? 'Сарын төлбөр' : 
                   stats.pricingModel === 'hybrid' ? 'Сарын + Нэгж' : 'Нэгж шимтгэл'}
                </div>
                <div className="text-xl font-bold text-gray-900">
                  {stats.pricingModel === 'monthly_fixed' 
                    ? `${(stats.monthlyFee || 0).toLocaleString()}₮/сар`
                    : stats.pricingModel === 'hybrid'
                    ? `${(stats.monthlyFee || 0).toLocaleString()}₮ + ${(stats.commissionValue || 0).toLocaleString()}₮`
                    : `${(company?.commission_value || 0).toLocaleString()}₮`
                  }
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Нийт шимтгэл</div>
              <div className="text-lg font-semibold text-gray-900">
                {(stats.totalCommission || 0).toLocaleString()}₮
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <div className="text-sm text-gray-600">Төлсөн</div>
              <div className="text-lg font-bold text-green-600">
                {(stats.totalPaid || 0).toLocaleString()}₮
              </div>
            </div>
            <div>
              <div className="text-sm text-gray-600">Төлөөгүй</div>
              <div className={`text-lg font-bold ${(stats.unpaidCommission || 0) > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {(stats.unpaidCommission || 0).toLocaleString()}₮
              </div>
            </div>
          </div>
          
          {(stats.unpaidCommission || 0) > 0 && (
            <button 
              onClick={() => setShowPaymentModal(true)} 
              className="w-full mt-4 btn bg-red-600 text-white hover:bg-red-700 justify-center"
            >
              💳 Төлбөр бүртгэх
            </button>
          )}
        </div>

        {/* Ерөнхий статистик */}
        <div className="bg-white rounded-2xl p-5 shadow-lg border border-gray-200 hover:-translate-y-1 transition-all">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span>📊</span>
            <span>Ерөнхий мэдээлэл</span>
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">{stats.userCount || 0}</div>
              <div className="text-sm text-gray-600">👥 Хэрэглэгчид</div>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-purple-600">{stats.warehouseCount || 0}</div>
              <div className="text-sm text-gray-600">🏭 Агуулах</div>
            </div>
            <div className="bg-green-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.shopCount || 0}</div>
              <div className="text-sm text-gray-600">🛍️ Онлайн шоп</div>
            </div>
            <div className="bg-yellow-50 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.totalOrders || 0}</div>
              <div className="text-sm text-gray-600">📋 Нийт захиалга</div>
            </div>
          </div>
        </div>
      </div>

      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
          <div className="flex items-start justify-between mb-4">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
              <span className="text-3xl">📦</span>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Өнөөдрийн</div>
              <div className="text-3xl font-bold text-gray-900">{todayDeliveries}</div>
              <div className="text-sm text-gray-500">хүргэлт</div>
            </div>
          </div>
          <div className="space-y-2 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">✅ Амжилттай</span>
              <span className="font-semibold text-gray-900">{todayCompleted}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">⏰ Хүлээгдэж буй</span>
              <span className="font-semibold text-gray-900">{todayPending}</span>
            </div>
          </div>
        </div>

        <div className={`rounded-2xl p-6 shadow-sm border transition ${unpaidCommission > 0 ? 'bg-red-50 border-red-200 hover:shadow-md' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-start justify-between mb-4">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${unpaidCommission > 0 ? 'bg-red-100' : 'bg-green-100'}`}>
              <span className="text-3xl">💰</span>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-600">Шимтгэл</div>
              <div className="text-2xl font-bold text-gray-900">{company.commission_value.toLocaleString()}₮</div>
              <div className="text-xs text-gray-600">хүргэлт тутамд</div>
            </div>
          </div>
          <div className="pt-4 border-t border-gray-200">
            {unpaidCommission > 0 ? (
              <>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-600">🔴 Төлөөгүй:</span>
                  <span className="text-xl font-bold text-red-600">{unpaidCommission.toLocaleString()}₮</span>
                </div>
                <button onClick={() => setShowPaymentModal(true)} className="w-full btn bg-red-600 text-white hover:bg-red-700 justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
                  <span>Төлбөр бүртгэх</span>
                </button>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-2xl">✅</span>
                  <span className="text-sm font-medium text-green-700">Бүх төлбөр төлөгдсөн</span>
                </div>
                <button disabled className="w-full btn bg-green-600 text-white opacity-50 cursor-not-allowed justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                  <span>Төлбөр төлөгдсөн</span>
                </button>
              </>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
          <div className="flex items-start justify-between mb-4">
            <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center">
              <span className="text-3xl">✅</span>
            </div>
            <div className="text-right">
              <div className="text-sm text-gray-500">Амжилт</div>
              <div className="text-3xl font-bold text-gray-900">{successRate}%</div>
              <div className="text-sm text-gray-500">{todayCompleted}/{todayDeliveries}</div>
            </div>
          </div>
          <div className="pt-4 border-t border-gray-100">
            <div className="mb-2 text-sm text-gray-600">Өдрийн прогресс</div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div className="bg-green-500 h-full rounded-full transition-all duration-500" style={{ width: `${successRate}%` }}></div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="admin-card hover:shadow-md transition"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center"><span className="text-2xl">👥</span></div><div><p className="text-xs text-gray-500">Хэрэглэгчид</p><p className="text-2xl font-bold text-gray-900">{stats.userCount || 0}</p></div></div></div>
        <div className="admin-card hover:shadow-md transition"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center"><span className="text-2xl">🏭</span></div><div><p className="text-xs text-gray-500">Агуулах</p><p className="text-2xl font-bold text-gray-900">{stats.warehouseCount || 0}</p></div></div></div>
        <div className="admin-card hover:shadow-md transition"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center"><span className="text-2xl">🛍️</span></div><div><p className="text-xs text-gray-500">Онлайн шоп</p><p className="text-2xl font-bold text-gray-900">{stats.shopCount || 0}</p></div></div></div>
        <div className="admin-card hover:shadow-md transition"><div className="flex items-center gap-3"><div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center"><span className="text-2xl">📊</span></div><div><p className="text-xs text-gray-500">Нийт захиалга</p><p className="text-2xl font-bold text-gray-900">{stats.totalOrders || 0}</p></div></div></div>
      </div> */}

      <div className="admin-card mb-6">
        <div className="flex gap-4 border-b border-gray-200">
          <button onClick={() => setActiveTab('overview')} className={`px-4 py-3 font-medium transition ${activeTab === 'overview' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}>📋 Ерөнхий</button>
          <button onClick={() => setActiveTab('users')} className={`px-4 py-3 font-medium transition ${activeTab === 'users' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}>👥 Хэрэглэгчид ({stats.userCount || 0})</button>
          <button onClick={() => setActiveTab('warehouses')} className={`px-4 py-3 font-medium transition ${activeTab === 'warehouses' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}>🏭 Агуулахууд ({stats.warehouseCount || 0})</button>
          <button onClick={() => setActiveTab('shops')} className={`px-4 py-3 font-medium transition ${activeTab === 'shops' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-500 hover:text-gray-700'}`}>🛍️ Онлайн шопууд ({stats.shopCount || 0})</button>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">🏢</span>
              <span>Компанийн мэдээлэл</span>
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Регистр:</span><span className="font-medium text-gray-900">{company.register_number}</span></div>
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Утас:</span><span className="font-medium text-gray-900">{company.phone}</span></div>
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">И-мэйл:</span><span className="font-medium text-gray-900">{company.email || '-'}</span></div>
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Хаяг:</span><span className="font-medium text-gray-900 text-right">{company.address || '-'}</span></div>
              <div className="flex justify-between py-2"><span className="text-gray-600">Үүссэн:</span><span className="font-medium text-gray-900">{new Date(company.created_at).toLocaleDateString('mn-MN')}</span></div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-2xl">⚙️</span>
              <span>Комиссын тохиргоо</span>
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Төрөл:</span><span className="font-medium text-gray-900">{company.commission_type === 'per_delivery' ? 'Хүргэлт тутамд' : 'Сарын'}</span></div>
              <div className="flex justify-between py-2 border-b border-gray-100"><span className="text-gray-600">Дүн:</span><span className="font-medium text-lg text-primary-600">{company.commission_value.toLocaleString()}₮</span></div>
              <div className="flex justify-between py-2"><span className="text-gray-600">Төлбөрийн давтамж:</span><span className="font-medium text-gray-900">{company.billing_cycle === 'daily' ? 'Өдөр бүр' : company.billing_cycle === 'weekly' ? '7 хоног бүр' : 'Сар бүр'}</span></div>
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">ℹ️ Өмнөх тайлан болон төлбөрийн дэлгэрэнгүй түүхийг <strong>Тайлан</strong> цэснээс харна уу.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Компанийн хэрэглэгчид</h3>
          {users.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><span className="text-5xl mb-4 block">👥</span><p>Хэрэглэгч бүртгэгдээгүй байна</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>ID</th><th>Нэр</th><th>Username</th><th>Утас</th><th>Роль</th><th>Статус</th><th>Үүссэн</th></tr></thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="font-semibold text-gray-500">#{user.id}</td>
                      <td className="font-medium">{user.full_name}</td>
                      <td className="font-mono text-sm">{user.username}</td>
                      <td>{user.phone}</td>
                      <td><span className="badge badge-info">{user.role === 'company_admin' ? 'Админ' : user.role}</span></td>
                      <td><span className={`badge ${user.status === 'active' ? 'badge-success' : 'badge-gray'}`}>{user.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}</span></td>
                      <td className="text-sm text-gray-600">{new Date(user.created_at).toLocaleDateString('mn-MN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'warehouses' && (
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Компанийн агуулахууд</h3>
          {warehouses.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><span className="text-5xl mb-4 block">🏭</span><p>Агуулах бүртгэгдээгүй байна</p><p className="text-sm mt-2">Компанийн админ агуулах бүртгэнэ</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {warehouses.map((warehouse) => (
                <div key={warehouse.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{warehouse.warehouse_name}</h4>
                    <span className={`badge text-xs ${warehouse.status === 'active' ? 'badge-success' : 'badge-gray'}`}>{warehouse.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">📍 {warehouse.address || 'Хаяг байхгүй'}</p>
                  <p className="text-xs text-gray-500 mt-2">Үүссэн: {new Date(warehouse.created_at).toLocaleDateString('mn-MN')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'shops' && (
        <div className="admin-card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Онлайн шопууд</h3>
          {shops.length === 0 ? (
            <div className="text-center py-12 text-gray-500"><span className="text-5xl mb-4 block">🛍️</span><p>Онлайн шоп бүртгэгдээгүй байна</p><p className="text-sm mt-2">Компанийн админ онлайн шоп бүртгэнэ</p></div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {shops.map((shop) => (
                <div key={shop.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">{shop.shop_name}</h4>
                    <span className={`badge text-xs ${shop.status === 'active' ? 'badge-success' : 'badge-gray'}`}>{shop.status === 'active' ? 'Идэвхтэй' : 'Идэвхгүй'}</span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{shop.shop_type === 'facebook' ? '📘 Facebook' : shop.shop_type === 'instagram' ? '📸 Instagram' : shop.shop_type === 'website' ? '🌐 Website' : shop.shop_type}</p>
                  <p className="text-xs text-gray-500">🏭 {shop.warehouse?.warehouse_name || 'Агуулах холбогдоогүй'}</p>
                  <p className="text-xs text-gray-500 mt-2">Үүссэн: {new Date(shop.created_at).toLocaleDateString('mn-MN')}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <span className="text-3xl">💰</span>
                  <span>Төлбөр бүртгэх</span>
                </h2>
                <button onClick={() => setShowPaymentModal(false)} className="text-gray-400 hover:text-gray-600">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Нийт төлөх дүн:</label>
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 text-center">
                  <p className="text-3xl font-bold text-red-600">{unpaidCommission.toLocaleString()}₮</p>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Төлсөн дүн:</label>
                <input type="number" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="Дүн оруулах..." className="admin-input text-lg font-semibold" autoFocus />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Үлдэгдэл:</label>
                <div className={`border-2 rounded-xl p-4 text-center ${remaining <= 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <p className={`text-3xl font-bold ${remaining <= 0 ? 'text-green-600' : 'text-yellow-600'}`}>{remaining > 0 ? remaining.toLocaleString() : 0}₮</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowPaymentModal(false)} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200 flex-1 justify-center">Болих</button>
                <button onClick={handlePayment} className="btn btn-primary flex-1 justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  <span>Төлсөн бүртгэх</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default CompanyDetails;